import React from 'react';
import { Paper, Typography, Box, CircularProgress, Grid } from '@mui/material';
import Chart from 'react-apexcharts';
import { formatCurrency } from '../../../utils/formatCurrency';
import { DASHBOARD_COLORS, sectionPaperSx } from '../dashboardStyles';
import { getSalesOverviewKpiConfig, buildBaseChartOptions } from './salesOverviewConstants';
import SalesOverviewHeader from './SalesOverviewHeader';

/** Template 3 — line chart (left) + stacked metric panel (right). */
const SalesOverviewTemplate3 = ({
  labels,
  data,
  metrics,
  loading,
  compact = false,
  period = 'monthly',
  onPeriodChange,
  showAiAssistant = false,
  onAiAssistantClick,
}) => {
  const kpiConfig = getSalesOverviewKpiConfig(period);
  const chartHeight = compact ? 140 : 260;
  const chartOptions = {
    ...buildBaseChartOptions(labels),
    colors: ['#0d9488'],
    stroke: { curve: 'straight', width: 2.5 },
    markers: { size: compact ? 2 : 3, colors: ['#0d9488'] },
    tooltip: { y: { formatter: (v) => formatCurrency(v) } },
  };

  const series = [{ name: 'Sales', data: data || [] }];

  return (
    <Paper
      elevation={0}
      sx={{
        ...sectionPaperSx,
        height: '100%',
        minHeight: compact ? 0 : 320,
        display: 'flex',
        flexDirection: 'column',
        p: compact ? 1.5 : 2.5,
      }}
    >
      <SalesOverviewHeader
        period={period}
        onPeriodChange={onPeriodChange}
        compact={compact}
        showPeriodToggle={!compact && Boolean(onPeriodChange)}
        showAiAssistant={showAiAssistant}
        onAiAssistantClick={onAiAssistantClick}
      />

      <Grid container spacing={compact ? 1 : 2} sx={{ flex: 1 }} alignItems="stretch">
        <Grid item xs={12} md={compact ? 12 : 8}>
          <Box
            sx={{
              height: '100%',
              minHeight: compact ? 100 : 200,
              border: `1px solid ${DASHBOARD_COLORS.border}`,
              borderRadius: 1.5,
              p: compact ? 0.5 : 1,
              bgcolor: '#f8fafc',
            }}
          >
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress size={compact ? 24 : 32} />
              </Box>
            ) : (
              <Chart options={chartOptions} series={series} type="line" height={chartHeight} />
            )}
          </Box>
        </Grid>
        <Grid item xs={12} md={compact ? 12 : 4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: compact ? 0.75 : 1.25, height: '100%' }}>
            {kpiConfig.map((kpi) => (
              <Box
                key={kpi.key}
                sx={{
                  flex: 1,
                  p: compact ? 1 : 1.5,
                  borderRadius: 1.5,
                  border: `1px solid ${DASHBOARD_COLORS.border}`,
                  bgcolor: DASHBOARD_COLORS.surface,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: DASHBOARD_COLORS.textMuted,
                    fontWeight: 600,
                    fontSize: compact ? '0.55rem' : '0.75rem',
                    mb: 0.25,
                  }}
                >
                  {kpi.label}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: compact ? '0.75rem' : '1.1rem',
                    color: kpi.color,
                    lineHeight: 1.2,
                  }}
                >
                  {loading ? '—' : formatCurrency(metrics[kpi.key] ?? 0)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SalesOverviewTemplate3;
